/**
 * ClientSelector - Component for selecting a single client
 */
import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { clientsApi } from '../../services/clientsApi';
import { getTextFieldStyles, getAutocompleteStyles, getAutocompleteListboxStyles } from '../../styles/formStyles';

interface ClientSelectorProps {
  value: number | null;
  onChange: (clientId: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
}

const ClientSelector: React.FC<ClientSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = '',
  label,
  error = false,
  helperText,
  required = false,
}) => {
  // Fetch all clients
  const { data: clientsData, isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.getClients({ page_size: 1000 }), // Get all clients
  });

  const clients = clientsData?.results || [];

  const selectedClient = clients.find((c) => c.id === value) || null;

  const handleChange = (_event: any, newValue: typeof selectedClient) => {
    onChange(newValue ? newValue.id : null);
  };

  return (
    <Autocomplete
      options={clients}
      value={selectedClient}
      onChange={handleChange}
      disabled={disabled || isLoading}
      loading={isLoading}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          required={required}
          fullWidth
          margin="normal"
          sx={getTextFieldStyles({ error })}
        />
      )}
      fullWidth
      sx={getAutocompleteStyles()}
      ListboxProps={{
        sx: getAutocompleteListboxStyles(),
      }}
    />
  );
};

export default ClientSelector;
